import { COUNTRIES } from "../countries";
import { CreateCounterpartyAddressBody } from "../schemas/counterpartyAddress.schema";
import { CreateCounterpartyContactBody } from "../schemas/counterpartyContact.schema";

const US_STATE_AND_ZIP = /^([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/;
const REGION_AND_POSTAL_CODE =
    /^([A-Za-z]{2,3})\s+([A-Za-z0-9]{3,4} ?[A-Za-z0-9]{3})$/;
const POSTAL_CODE_AND_CITY = /^(\d{4,5})\s+(.+)$/;

function parsedStateAndPostalCode(
    value: string,
    country: string,
): { state: string; postalCode: string } | null {
    const pattern =
        country === "US" ? US_STATE_AND_ZIP : REGION_AND_POSTAL_CODE;
    const match = pattern.exec(value);
    if (!match) {
        return null;
    }
    return {
        state: match[1].toUpperCase(),
        postalCode: match[2].toUpperCase(),
    };
}

function trailingCountryCode(value: string): string | null {
    const upper = value.toUpperCase();
    return (
        COUNTRIES.find(
            (country) =>
                country.name.toUpperCase() === upper || country.code3 === upper,
        )?.code ?? null
    );
}

function streetAndCity(
    parts: string[],
): Pick<CreateCounterpartyAddressBody, "street" | "city"> {
    const city = parts[parts.length - 1];
    return parts.length > 1
        ? { street: parts.slice(0, -1).join(", "), city }
        : { city };
}

export function parsePartyAddress(raw: string): CreateCounterpartyAddressBody {
    const normalized = raw
        .replace(/\s*\n\s*/g, ", ")
        .replace(/\s+/g, " ")
        .trim();
    const parts = normalized
        .split(",")
        .map((part) => part.trim())
        .filter((part) => part.length > 0);
    const printedCountry = trailingCountryCode(parts[parts.length - 1] ?? "");
    const country = printedCountry ?? "US";
    const addressParts = printedCountry ? parts.slice(0, -1) : parts;
    const lastPart = addressParts[addressParts.length - 1] ?? "";
    const leadingParts = addressParts.slice(0, -1);
    const stateAndPostalCode = parsedStateAndPostalCode(lastPart, country);

    if (stateAndPostalCode && leadingParts.length > 0) {
        return {
            ...streetAndCity(leadingParts),
            ...stateAndPostalCode,
            country,
            isPrimary: true,
        };
    }

    const postalCodeAndCity = printedCountry
        ? POSTAL_CODE_AND_CITY.exec(lastPart)
        : null;
    if (postalCodeAndCity) {
        return {
            ...(leadingParts.length > 0
                ? { street: leadingParts.join(", ") }
                : {}),
            city: postalCodeAndCity[2],
            postalCode: postalCodeAndCity[1],
            country,
            isPrimary: true,
        };
    }

    if (printedCountry) {
        return {
            street: addressParts.join(", "),
            country,
            isPrimary: true,
        };
    }

    return {
        street: raw.trim(),
        isPrimary: true,
    };
}

export function splitPersonName(name: string): {
    firstName: string;
    lastName: string | null;
} {
    const parts = name
        .trim()
        .split(/\s+/)
        .filter((part) => part.length > 0);
    const [firstName, ...rest] = parts;
    return {
        firstName: firstName ?? name.trim(),
        lastName: rest.length > 0 ? rest.join(" ") : null,
    };
}

function contactBody({
    email,
    phoneNumber,
    contactName,
    fallbackContactName,
    isPrimary,
}: {
    email: string | null;
    phoneNumber: string | null;
    contactName: string | null;
    fallbackContactName: string | null;
    isPrimary: boolean;
}): CreateCounterpartyContactBody {
    const name =
        contactName ?? (email || phoneNumber ? fallbackContactName : null);
    return {
        email,
        phoneNumber,
        ...(name ? splitPersonName(name) : {}),
        isPrimary,
    };
}

export function groupContactDetails({
    emails,
    phoneNumbers,
    contactNames,
    fallbackContactName = null,
    isFirstPrimary,
}: {
    emails: string[];
    phoneNumbers: string[];
    contactNames: string[];
    fallbackContactName?: string | null;
    isFirstPrimary: boolean;
}): {
    first: CreateCounterpartyContactBody | null;
    extras: CreateCounterpartyContactBody[];
} {
    const [firstEmail, ...extraEmails] = emails;
    const [firstPhone, ...extraPhones] = phoneNumbers;
    const [firstContactName, ...extraContactNames] = contactNames;
    const hasFirst = Boolean(firstEmail || firstPhone || firstContactName);
    return {
        first: hasFirst
            ? contactBody({
                  email: firstEmail ?? null,
                  phoneNumber: firstPhone ?? null,
                  contactName: firstContactName ?? null,
                  fallbackContactName,
                  isPrimary: isFirstPrimary,
              })
            : null,
        extras: [
            ...extraEmails.map((email) =>
                contactBody({
                    email,
                    phoneNumber: null,
                    contactName: null,
                    fallbackContactName,
                    isPrimary: false,
                }),
            ),
            ...extraPhones.map((phoneNumber) =>
                contactBody({
                    email: null,
                    phoneNumber,
                    contactName: null,
                    fallbackContactName,
                    isPrimary: false,
                }),
            ),
            ...extraContactNames.map((contactName) =>
                contactBody({
                    email: null,
                    phoneNumber: null,
                    contactName,
                    fallbackContactName,
                    isPrimary: false,
                }),
            ),
        ],
    };
}
